import React, { useEffect, useRef, useState } from 'react';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon, RotateCcwIcon } from 'lucide-react';
interface CustomVideoPlayerProps {
  src: string;
}
export function CustomVideoPlayer({
  src
}: CustomVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showUnmuteOverlay, setShowUnmuteOverlay] = useState(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Attempt to autoplay with sound
    video.muted = false;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        // Autoplay with sound succeeded
        setIsPlaying(true);
        setIsMuted(false);
        setShowUnmuteOverlay(false);
      }).catch(() => {
        // Autoplay with sound failed, fallback to muted autoplay
        video.muted = true;
        video.play().then(() => {
          setIsPlaying(true);
          setIsMuted(true);
          setShowUnmuteOverlay(true);
        }).catch(() => {
          // If even muted autoplay fails (e.g., low power mode), just show play button
          setIsPlaying(false);
          setIsMuted(true);
          setShowUnmuteOverlay(false);
        });
      });
    }
  }, []);
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
      if (showUnmuteOverlay) {
        setShowUnmuteOverlay(false);
      }
    }
  };
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentProgress = videoRef.current.currentTime / videoRef.current.duration * 100;
      setProgress(currentProgress);
    }
  };
  const handleRewind = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
    }
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      videoRef.current.currentTime = percentage * videoRef.current.duration;
    }
  };
  return <div className="relative w-full overflow-hidden rounded-[14px] shadow-panel group" style={{
    aspectRatio: '1/1'
  }} data-id="element-208" data-mp="zxw5t8mimx0a">
      <video ref={videoRef} src={src} className="absolute inset-0 h-full w-full object-cover" playsInline loop onTimeUpdate={handleTimeUpdate} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} data-id="element-209" data-mp="1dpaqdjw2n69g" />

      {/* Unmute Overlay */}
      {showUnmuteOverlay && <div className="absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity" data-id="element-210" data-mp="2rucqp7l4or6s">
          <button onClick={toggleMute} className="flex items-center gap-2 rounded-full bg-brand-green px-6 py-3 font-bold text-ink-950 shadow-lg transition-transform hover:scale-105 active:scale-95" data-id="element-211" data-mp="2wmxiy4odyw87">
            <Volume2Icon className="h-5 w-5" data-id="element-212" data-mp="2nrprumq1i603" />
            Activar sonido
          </button>
        </div>}

      {/* Custom Controls (visible on hover or always visible at bottom) */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100" data-id="element-213" data-mp="3fk70fga5hzmt">
        
        {/* Progress Bar */}
        <div className="mb-4 h-1.5 w-full cursor-pointer rounded-full bg-white/30" onClick={handleProgressClick} data-id="element-214" data-mp="36m4ucmbk8gso">
          <div className="h-full rounded-full bg-brand-green transition-all duration-100 ease-linear" style={{
          width: `${progress}%`
        }} data-id="element-215" data-mp="1v5estgi30d5e" />
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between text-white" data-id="element-216" data-mp="25n9zauws2u2b">
          <div className="flex items-center gap-4" data-id="element-217" data-mp="21lyhhbk5bd4o">
            <button onClick={togglePlay} className="transition-transform hover:scale-110 active:scale-95" data-id="element-218" data-mp="21mq2jsg49sgp">
              {isPlaying ? <PauseIcon className="h-6 w-6 fill-current" data-id="element-219" data-mp="1wlkbdz23ud9a" /> : <PlayIcon className="h-6 w-6 fill-current" data-id="element-220" data-mp="2tcmf4dmm20dl" />}
            </button>
            <button onClick={handleRewind} className="transition-transform hover:scale-110 active:scale-95" title="Retroceder 10s" data-id="element-221" data-mp="2pbj01tce08df">
              <RotateCcwIcon className="h-5 w-5" data-id="element-222" data-mp="2kbej627khbmo" />
            </button>
          </div>
          
          <button onClick={toggleMute} className="transition-transform hover:scale-110 active:scale-95" data-id="element-223" data-mp="2fdd2fnxm5oed">
            {isMuted ? <VolumeXIcon className="h-6 w-6" data-id="element-224" data-mp="18z3xsgv5khs9" /> : <Volume2Icon className="h-6 w-6" data-id="element-225" data-mp="13yzgwpqc1l1i" />}
          </button>
        </div>
      </div>
    </div>;
}